import React, { useState, useEffect } from 'react';
import Modal from '../../comun/Modal';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';


const Impresoras = () => {
    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPrinter, setEditingPrinter] = useState(null);

    const initialPrinterState = {
        name: '',
        printer_type: 'thermal',
        connection_type: 'usb',
        connection_string: '',
        paper_width: 80,
        characters_per_line: 42,
        has_cash_drawer: true,
        is_active: true,
        is_default: false,
        config: { prints_receipt: true, prints_command: false }
    };

    const [printerForm, setPrinterForm] = useState(initialPrinterState);

    const DEFAULT_TICKET_CONFIG = {
        document_type_preset: 'nota_venta',
        custom_title: 'NOTA DE VENTA',
        show_document_title: true,
        show_order_seq_header: true,
        show_company_logo: true,
        show_company_name: true,
        show_tax_id: true,
        show_company_address: true,
        show_company_phone: true,
        show_accounting_obligation: false,
        show_tax_regime: false,
        show_artisan_qualification: false,
        show_date_time: true,
        show_sri_doc_number: false,
        show_order_number: true,
        show_sri_status: false,
        show_access_key: false,
        show_customer_info: true,
        show_customer_dni: true,
        show_table_info: true,
        show_item_notes: true,
        show_subtotal: true,
        show_discount: true,
        show_vat: false,
        show_total: true,
        show_total_cop: true,
        show_payment_method: true,
        show_footer_message: true,
        show_developer_credits: false,
        show_koa_logo: false,
    };

    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [activeSettingsTab, setActiveSettingsTab] = useState('general');
    const [settingsForm, setSettingsForm] = useState({
        company_name: '',
        company_address: '',
        company_phone: '',
        tax_id: '',
        obligation_contable: 'NO',
        contribuyente_regimen: '',
        artisan_qualification: '',
        receipt_header: '',
        receipt_footer: '',
        developer_credits: '',
        company_logo: '',
        koa_logo: '',
        ticket_config: { ...DEFAULT_TICKET_CONFIG }
    });

    const fetchPrinters = async () => {
        try {
            setLoading(true);
            const data = await printerService.getPrinters();
            setPrinters(data.results || data || []);
            setError('');
        } catch (err) {
            console.error('Error fetching printers:', err);
            setError('Error al cargar las impresoras');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = (e, fieldName) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxWidth = fieldName === 'koa_logo' ? 120 : 250;
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                const optimizedBase64 = canvas.toDataURL('image/png');
                setSettingsForm(prev => ({
                    ...prev,
                    [fieldName]: optimizedBase64
                }));
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const fetchSettings = async () => {
        try {
            const billingData = await billingService.getConfig();
            const printerData = await printerService.getSettings().catch(() => null);

            const mergedTicketConfig = {
                ...DEFAULT_TICKET_CONFIG,
                ...(billingData?.ticket_config || {})
            };

            setSettingsForm({
                company_name: billingData?.business_name !== undefined && billingData?.business_name !== null ? billingData.business_name : (printerData?.company_name || ''),
                company_address: billingData?.establishment_address !== undefined && billingData?.establishment_address !== null ? billingData.establishment_address : (printerData?.company_address || ''),
                company_phone: billingData?.company_phone !== undefined && billingData?.company_phone !== null ? billingData.company_phone : (printerData?.company_phone || ''),
                tax_id: billingData?.ruc !== undefined && billingData?.ruc !== null ? billingData.ruc : (printerData?.tax_id || ''),
                obligation_contable: billingData?.obligation_contable || 'NO',
                contribuyente_regimen: billingData?.contribuyente_regimen !== undefined && billingData?.contribuyente_regimen !== null ? billingData.contribuyente_regimen : '',
                artisan_qualification: billingData?.artisan_qualification !== undefined && billingData?.artisan_qualification !== null ? billingData.artisan_qualification : '',
                receipt_header: printerData?.receipt_header || '',
                receipt_footer: billingData?.footer_message !== undefined && billingData?.footer_message !== null ? billingData.footer_message : (printerData?.receipt_footer || ''),
                developer_credits: billingData?.developer_credits !== undefined && billingData?.developer_credits !== null ? billingData.developer_credits : '',
                company_logo: billingData?.company_logo || '',
                koa_logo: billingData?.koa_logo || '',
                ticket_config: mergedTicketConfig
            });
        } catch (err) {
            console.error('Error fetching settings:', err);
        }
    };

    useEffect(() => {
        fetchPrinters();
        fetchSettings();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPrinterForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleConfigChange = (e) => {
        const { name, checked } = e.target;
        setPrinterForm(prev => ({
            ...prev,
            config: {
                ...prev.config,
                [name]: checked
            }
        }));
    };

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettingsForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTicketToggle = (key) => {
        setSettingsForm(prev => ({
            ...prev,
            ticket_config: {
                ...prev.ticket_config,
                [key]: !prev.ticket_config[key]
            }
        }));
    };

    const handleTicketConfigField = (key, value) => {
        setSettingsForm(prev => ({
            ...prev,
            ticket_config: {
                ...prev.ticket_config,
                [key]: value
            }
        }));
    };

    const applyPreset = (presetType) => {
        if (presetType === 'nota_venta') {
            setSettingsForm(prev => ({
                ...prev,
                ticket_config: {
                    ...prev.ticket_config,
                    document_type_preset: 'nota_venta',
                    custom_title: 'NOTA DE VENTA',
                    show_document_title: true,
                    show_order_seq_header: true,
                    show_company_logo: true,
                    show_company_name: true,
                    show_tax_id: true,
                    show_company_address: true,
                    show_company_phone: true,
                    show_accounting_obligation: false,
                    show_tax_regime: false,
                    show_artisan_qualification: false,
                    show_date_time: true,
                    show_sri_doc_number: false,
                    show_order_number: true,
                    show_sri_status: false,
                    show_access_key: false,
                    show_customer_info: true,
                    show_customer_dni: true,
                    show_table_info: true,
                    show_item_notes: true,
                    show_subtotal: true,
                    show_discount: true,
                    show_vat: false,
                    show_total: true,
                    show_total_cop: true,
                    show_payment_method: true,
                    show_footer_message: true,
                    show_developer_credits: false,
                    show_koa_logo: false,
                }
            }));
        } else if (presetType === 'ticket_express') {
            setSettingsForm(prev => ({
                ...prev,
                ticket_config: {
                    ...prev.ticket_config,
                    document_type_preset: 'ticket_express',
                    custom_title: 'TICKET EXPRESS',
                    show_document_title: true,
                    show_order_seq_header: true,
                    show_company_logo: false,
                    show_company_name: true,
                    show_tax_id: false,
                    show_company_address: false,
                    show_company_phone: true,
                    show_accounting_obligation: false,
                    show_tax_regime: false,
                    show_artisan_qualification: false,
                    show_date_time: true,
                    show_sri_doc_number: false,
                    show_order_number: true,
                    show_sri_status: false,
                    show_access_key: false,
                    show_customer_info: true,
                    show_customer_dni: false,
                    show_table_info: true,
                    show_item_notes: true,
                    show_subtotal: false,
                    show_discount: true,
                    show_vat: false,
                    show_total: true,
                    show_total_cop: false,
                    show_payment_method: true,
                    show_footer_message: true,
                    show_developer_credits: false,
                    show_koa_logo: false,
                }
            }));
        } else if (presetType === 'factura') {
            setSettingsForm(prev => ({
                ...prev,
                ticket_config: {
                    ...prev.ticket_config,
                    document_type_preset: 'factura',
                    custom_title: 'FACTURA ELECTRÓNICA',
                    show_document_title: true,
                    show_order_seq_header: true,
                    show_company_logo: true,
                    show_company_name: true,
                    show_tax_id: true,
                    show_company_address: true,
                    show_company_phone: true,
                    show_accounting_obligation: true,
                    show_tax_regime: true,
                    show_artisan_qualification: true,
                    show_date_time: true,
                    show_sri_doc_number: true,
                    show_order_number: true,
                    show_sri_status: true,
                    show_access_key: true,
                    show_customer_info: true,
                    show_customer_dni: true,
                    show_table_info: true,
                    show_item_notes: true,
                    show_subtotal: true,
                    show_discount: true,
                    show_vat: true,
                    show_total: true,
                    show_total_cop: true,
                    show_payment_method: true,
                    show_footer_message: true,
                    show_developer_credits: true,
                    show_koa_logo: true,
                }
            }));
        }
    };

    const openSettingsModal = () => {
        fetchSettings();
        setIsSettingsModalOpen(true);
    };

    const handleSettingsSubmit = async (e) => {
        e.preventDefault();
        try {
            // 1. Guardar en printer service
            await printerService.updateSettings({
                company_name: settingsForm.company_name,
                company_address: settingsForm.company_address,
                company_phone: settingsForm.company_phone,
                tax_id: settingsForm.tax_id,
                receipt_header: settingsForm.receipt_header,
                receipt_footer: settingsForm.receipt_footer
            });

            // 2. Guardar en ElectronicBillingConfig para sincronía total
            const currentBilling = await billingService.getConfig();
            await billingService.saveConfig({
                ...currentBilling,
                business_name: settingsForm.company_name,
                ruc: settingsForm.tax_id,
                establishment_address: settingsForm.company_address,
                company_phone: settingsForm.company_phone,
                obligation_contable: settingsForm.obligation_contable,
                contribuyente_regimen: settingsForm.contribuyente_regimen,
                artisan_qualification: settingsForm.artisan_qualification,
                footer_message: settingsForm.receipt_footer,
                developer_credits: settingsForm.developer_credits,
                company_logo: settingsForm.company_logo,
                koa_logo: settingsForm.koa_logo,
                ticket_config: settingsForm.ticket_config
            });

            setIsSettingsModalOpen(false);
            alert('✅ ¡Configuración del ticket guardada exitosamente!');
        } catch (err) {
            console.error('Error saving settings:', err);
            alert('Error al guardar la configuración');
        }
    };



    const openNewModal = () => {
        setEditingPrinter(null);
        setPrinterForm(initialPrinterState);
        setIsModalOpen(true);
    };

    const openEditModal = (printer) => {
        setEditingPrinter(printer);
        setPrinterForm({
            name: printer.name || '',
            printer_type: printer.printer_type || 'thermal',
            connection_type: printer.connection_type || 'usb',
            connection_string: printer.connection_string || '',
            paper_width: printer.paper_width || 80,
            characters_per_line: printer.characters_per_line || 42,
            has_cash_drawer: printer.has_cash_drawer !== undefined ? printer.has_cash_drawer : true,
            is_active: printer.is_active !== undefined ? printer.is_active : true,
            is_default: printer.is_default !== undefined ? printer.is_default : false,
            config: {
                prints_receipt: printer.config?.prints_receipt !== undefined ? printer.config.prints_receipt : true,
                prints_command: printer.config?.prints_command !== undefined ? printer.config.prints_command : false
            }
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPrinter) {
                await printerService.updatePrinter(editingPrinter.id, printerForm);
            } else {
                await printerService.createPrinter(printerForm);
            }
            setIsModalOpen(false);
            fetchPrinters();
        } catch (err) {
            console.error('Error saving printer:', err);
            alert('Error al guardar la impresora. Verifique los datos.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta impresora?')) {
            try {
                await printerService.deletePrinter(id);
                fetchPrinters();
            } catch (err) {
                console.error('Error deleting printer:', err);
                alert('Error al eliminar la impresora');
            }
        }
    };

    const tCfg = settingsForm.ticket_config || DEFAULT_TICKET_CONFIG;

    return (
        <div className="page-container">
            <div className="page-header">
                <h2>Administración de Impresoras</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn btn-secondary" onClick={openSettingsModal}>
                        <i className="bi bi-sliders" style={{ marginRight: '6px' }}></i>
                        Configurar Formato Ticket
                    </button>
                    <button className="btn btn-primary" onClick={openNewModal}>
                        <i className="bi bi-plus-circle" style={{ marginRight: '6px' }}></i>
                        Nueva Impresora
                    </button>
                </div>
            </div>

            {loading ? <div>Cargando impresoras...</div> : error ? <div className="alert alert-error">{error}</div> : (
                <div className="table-responsive">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Tipo</th>
                                <th>Conexión</th>
                                <th>Papel (mm)</th>
                                <th>Caja</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {printers.length === 0 ? (
                                <tr><td colSpan="7">No hay impresoras registradas</td></tr>
                            ) : (
                                printers.map(printer => (
                                    <tr key={printer.id}>
                                        <td>{printer.name} {printer.is_default && <span style={{fontSize: '0.8em', color: 'green'}}>(Default)</span>}</td>
                                        <td>{printer.printer_type === 'thermal' ? 'Térmica' : printer.printer_type}</td>
                                        <td>{printer.connection_type === 'rawbt' ? 'App KOAPrint (Tablet)' : printer.connection_type.toUpperCase()}</td>
                                        <td>{printer.paper_width}mm</td>
                                        <td>{printer.has_cash_drawer ? 'Sí' : 'No'}</td>
                                        <td>{printer.is_active ? 'Activa' : 'Inactiva'}</td>
                                        <td>
                                            <button 
                                                className="btn btn-secondary" 
                                                onClick={() => openEditModal(printer)}
                                                style={{ marginRight: '5px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                            >
                                                Editar
                                            </button>
                                            <button 
                                                className="btn btn-danger" 
                                                onClick={() => handleDelete(printer.id)}
                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                            >
                                                Eliminar
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPrinter ? "Editar Impresora" : "Nueva Impresora"}>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Nombre de Impresora</label>
                        <input
                            type="text"
                            name="name"
                            value={printerForm.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    
                    <div className="form-group">
                        <label>Tipo de Conexión</label>
                        <select name="connection_type" value={printerForm.connection_type} onChange={handleInputChange} className="form-control">
                            <option value="usb">USB (Agente PC)</option>
                            <option value="network">Red / IP (Agente PC)</option>
                            <option value="bluetooth">Bluetooth (Agente PC)</option>
                            <option value="serial">Serial / COM (Agente PC)</option>
                            <option value="rawbt">Inalámbrica (KOAPrint Tablet)</option>
                        </select>
                    </div>

                    {printerForm.connection_type !== 'rawbt' && (
                        <div className="form-group">
                            <label>Cadena de Conexión</label>
                            <input
                                type="text"
                                name="connection_string"
                                value={printerForm.connection_string}
                                onChange={handleInputChange}
                                placeholder="Ej: /dev/usb/lp0, 192.168.1.100, COM1"
                            />
                        </div>
                    )}

                    <div className="form-group" style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <label>Ancho de Papel (mm)</label>
                            <select name="paper_width" value={printerForm.paper_width} onChange={handleInputChange} className="form-control">
                                <option value="58">58mm</option>
                                <option value="80">80mm</option>
                            </select>
                        </div>
                        <div style={{ flex: 1 }}>
                            <label>Caracteres por línea</label>
                            <input
                                type="number"
                                name="characters_per_line"
                                value={printerForm.characters_per_line}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="has_cash_drawer"
                                checked={printerForm.has_cash_drawer}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            Tiene Caja Registradora
                        </label>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="is_default"
                                checked={printerForm.is_default}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            Es la Impresora Principal (Por Defecto)
                        </label>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={printerForm.is_active}
                                onChange={handleInputChange}
                                style={{ marginRight: '8px' }}
                            />
                            Activa
                        </label>
                    </div>

                    <h4 style={{ margin: '1.5rem 0 0.5rem 0', color: 'var(--primary-color)' }}>Documentos a Imprimir</h4>
                    <p style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '1rem' }}>Seleccione qué documentos debe imprimir esta impresora</p>
                    
                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="prints_receipt"
                                checked={printerForm.config?.prints_receipt ?? true}
                                onChange={handleConfigChange}
                                style={{ marginRight: '8px' }}
                            />
                            Imprimir Factura/Ticket (Con detalles y precios para el cliente)
                        </label>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="prints_command"
                                checked={printerForm.config?.prints_command ?? false}
                                onChange={handleConfigChange}
                                style={{ marginRight: '8px' }}
                            />
                            Imprimir Comanda (Sin precios, para cocina o preparación)
                        </label>
                    </div>

                    <div className="form-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary">Guardar Impresora</button>
                    </div>
                </form>
            </Modal>

            <Modal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} title="Personalización del Formato de Ticket" maxWidth="1050px">
                <form onSubmit={handleSettingsSubmit}>
                    {/* BARRA DE PLANTILLAS PREESTABLECIDAS */}
                    <div style={{
                        backgroundColor: '#f8fafc',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                    }}>
                        <div>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                                <i className="bi bi-lightning-charge-fill" style={{ color: '#eab308', marginRight: '6px' }}></i>
                                Plantillas Rápidas:
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: '6px' }}>
                                Seleccione un formato base con un solo clic
                            </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => applyPreset('nota_venta')}
                                className="btn"
                                style={{
                                    fontSize: '0.82rem',
                                    padding: '6px 12px',
                                    backgroundColor: tCfg.document_type_preset === 'nota_venta' ? '#10b981' : '#ecfdf5',
                                    color: tCfg.document_type_preset === 'nota_venta' ? '#ffffff' : '#047857',
                                    border: '1px solid #a7f3d0',
                                    fontWeight: 600
                                }}
                            >
                                <i className="bi bi-file-text-fill" style={{ marginRight: '4px' }}></i> Nota de Venta (Limpia)
                            </button>
                            <button
                                type="button"
                                onClick={() => applyPreset('ticket_express')}
                                className="btn"
                                style={{
                                    fontSize: '0.82rem',
                                    padding: '6px 12px',
                                    backgroundColor: tCfg.document_type_preset === 'ticket_express' ? '#3b82f6' : '#eff6ff',
                                    color: tCfg.document_type_preset === 'ticket_express' ? '#ffffff' : '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    fontWeight: 600
                                }}
                            >
                                <i className="bi bi-receipt-cutoff" style={{ marginRight: '4px' }}></i> Ticket Express (Corto)
                            </button>
                            <button
                                type="button"
                                onClick={() => applyPreset('factura')}
                                className="btn"
                                style={{
                                    fontSize: '0.82rem',
                                    padding: '6px 12px',
                                    backgroundColor: tCfg.document_type_preset === 'factura' ? '#6366f1' : '#eef2ff',
                                    color: tCfg.document_type_preset === 'factura' ? '#ffffff' : '#4338ca',
                                    border: '1px solid #c7d2fe',
                                    fontWeight: 600
                                }}
                            >
                                <i className="bi bi-bank2" style={{ marginRight: '4px' }}></i> Factura SRI Completa
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 0.95fr', gap: '20px', alignItems: 'start' }}>
                        {/* PANEL IZQUIERDO: CONFIGURACIÓN POR SECCIONES */}
                        <div>
                            {/* PESTAÑAS DE NAVEGACIÓN */}
                            <div style={{
                                display: 'flex',
                                borderBottom: '2px solid #e2e8f0',
                                marginBottom: '14px',
                                gap: '4px'
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setActiveSettingsTab('general')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '0.82rem',
                                        fontWeight: activeSettingsTab === 'general' ? 700 : 500,
                                        color: activeSettingsTab === 'general' ? '#2563eb' : '#64748b',
                                        border: 'none',
                                        borderBottom: activeSettingsTab === 'general' ? '2px solid #2563eb' : '2px solid transparent',
                                        background: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🏢 Empresa & Encabezado
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSettingsTab('sri')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '0.82rem',
                                        fontWeight: activeSettingsTab === 'sri' ? 700 : 500,
                                        color: activeSettingsTab === 'sri' ? '#2563eb' : '#64748b',
                                        border: 'none',
                                        borderBottom: activeSettingsTab === 'sri' ? '2px solid #2563eb' : '2px solid transparent',
                                        background: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🏛️ Datos Fiscales / SRI
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSettingsTab('order_customer')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '0.82rem',
                                        fontWeight: activeSettingsTab === 'order_customer' ? 700 : 500,
                                        color: activeSettingsTab === 'order_customer' ? '#2563eb' : '#64748b',
                                        border: 'none',
                                        borderBottom: activeSettingsTab === 'order_customer' ? '2px solid #2563eb' : '2px solid transparent',
                                        background: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    🧾 Cliente & Pedido
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveSettingsTab('totals_footer')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '0.82rem',
                                        fontWeight: activeSettingsTab === 'totals_footer' ? 700 : 500,
                                        color: activeSettingsTab === 'totals_footer' ? '#2563eb' : '#64748b',
                                        border: 'none',
                                        borderBottom: activeSettingsTab === 'totals_footer' ? '2px solid #2563eb' : '2px solid transparent',
                                        background: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    💰 Totales & Pie
                                </button>
                            </div>

                            {/* CONTENIDO DE PESTAÑA: 1. EMPRESA Y ENCABEZADO */}
                            {activeSettingsTab === 'general' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div className="form-group" style={{ padding: '8px', border: '1px dashed #cbd5e1', borderRadius: '6px', backgroundColor: '#f8fafc' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                                                <i className="bi bi-image" style={{ marginRight: '4px', color: '#4a7c59' }}></i> Logo de la Empresa (Cabecera)
                                            </label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_company_logo}
                                                    onChange={() => handleTicketToggle('show_company_logo')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir Logo
                                            </label>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg"
                                                onChange={(e) => handleImageUpload(e, 'company_logo')}
                                                style={{ fontSize: '0.8rem' }}
                                            />
                                            {settingsForm.company_logo && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSettingsForm({ ...settingsForm, company_logo: '' })}
                                                    style={{ padding: '2px 6px', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                                                >
                                                    Quitar
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>Nombre / Razón Social</label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_company_name}
                                                    onChange={() => handleTicketToggle('show_company_name')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <input type="text" name="company_name" value={settingsForm.company_name} onChange={handleSettingsChange} placeholder="Ej: FROZEN" required />
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>RUC / Cédula</label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_tax_id}
                                                    onChange={() => handleTicketToggle('show_tax_id')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <input type="text" name="tax_id" value={settingsForm.tax_id} onChange={handleSettingsChange} placeholder="Ej: 0401788617001" required />
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>Dirección</label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_company_address}
                                                    onChange={() => handleTicketToggle('show_company_address')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <input type="text" name="company_address" value={settingsForm.company_address} onChange={handleSettingsChange} placeholder="Ej: Av. Principal 123" />
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>Teléfono de Contacto</label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_company_phone}
                                                    onChange={() => handleTicketToggle('show_company_phone')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <input type="text" name="company_phone" value={settingsForm.company_phone} onChange={handleSettingsChange} placeholder="Ej: 0999999999" />
                                    </div>

                                    <div className="form-group" style={{ backgroundColor: '#f0fdf4', padding: '10px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                                        <label style={{ cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: '#166534', fontWeight: 600 }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_order_seq_header}
                                                onChange={() => handleTicketToggle('show_order_seq_header')}
                                                style={{ marginRight: '8px', transform: 'scale(1.15)' }}
                                            />
                                            ⭐ Destacar Turno / Orden en Cabecera (ORDEN #)
                                        </label>
                                        <small style={{ display: 'block', color: '#15803d', marginTop: '2px', marginLeft: '24px', fontSize: '0.75rem' }}>
                                            Imprime un marco grande al inicio con el número de turno para fácil llamado al cliente.
                                        </small>
                                    </div>
                                </div>
                            )}

                            {/* CONTENIDO DE PESTAÑA: 2. DATOS FISCALES Y SRI */}
                            {activeSettingsTab === 'sri' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div className="form-group">
                                        <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Título del Comprobante en Ticket</label>
                                        <input
                                            type="text"
                                            value={tCfg.custom_title || ''}
                                            onChange={(e) => handleTicketConfigField('custom_title', e.target.value)}
                                            placeholder="Ej: NOTA DE VENTA, FACTURA, COMPROBANTE DE ENTREGA"
                                        />
                                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                                            {['NOTA DE VENTA', 'FACTURA ELECTRÓNICA', 'NOTA DE ENTREGA', 'TICKET'].map(tit => (
                                                <button
                                                    key={tit}
                                                    type="button"
                                                    onClick={() => handleTicketConfigField('custom_title', tit)}
                                                    style={{
                                                        fontSize: '0.72rem',
                                                        padding: '2px 6px',
                                                        backgroundColor: tCfg.custom_title === tit ? '#dbeafe' : '#f1f5f9',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {tit}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Campos Fiscales Opcionales (Activar solo si desea que salgan en el papel):
                                        </span>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={tCfg.show_accounting_obligation}
                                                        onChange={() => handleTicketToggle('show_accounting_obligation')}
                                                        style={{ marginRight: '8px' }}
                                                    />
                                                    Obligado a llevar contabilidad
                                                </label>
                                                <select
                                                    name="obligation_contable"
                                                    value={settingsForm.obligation_contable}
                                                    onChange={handleSettingsChange}
                                                    style={{ width: '80px', padding: '2px 4px', fontSize: '0.8rem' }}
                                                >
                                                    <option value="NO">NO</option>
                                                    <option value="SI">SI</option>
                                                </select>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={tCfg.show_tax_regime}
                                                        onChange={() => handleTicketToggle('show_tax_regime')}
                                                        style={{ marginRight: '8px' }}
                                                    />
                                                    Régimen Tributario (RIMPE, etc.)
                                                </label>
                                                <input
                                                    type="text"
                                                    name="contribuyente_regimen"
                                                    value={settingsForm.contribuyente_regimen}
                                                    onChange={handleSettingsChange}
                                                    placeholder="RIMPE - NEGOCIO POPULAR"
                                                    style={{ width: '180px', padding: '2px 6px', fontSize: '0.8rem' }}
                                                />
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={tCfg.show_artisan_qualification}
                                                        onChange={() => handleTicketToggle('show_artisan_qualification')}
                                                        style={{ marginRight: '8px' }}
                                                    />
                                                    Calificación Artesanal No.
                                                </label>
                                                <input
                                                    type="text"
                                                    name="artisan_qualification"
                                                    value={settingsForm.artisan_qualification || ''}
                                                    onChange={handleSettingsChange}
                                                    placeholder="Ej: 123456"
                                                    style={{ width: '120px', padding: '2px 6px', fontSize: '0.8rem' }}
                                                />
                                            </div>

                                            <hr style={{ border: 0, borderTop: '1px dashed #e2e8f0', margin: '4px 0' }} />

                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_sri_doc_number}
                                                    onChange={() => handleTicketToggle('show_sri_doc_number')}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                Imprimir Número de Factura SRI (ej. 001-001-000000129)
                                            </label>

                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_sri_status}
                                                    onChange={() => handleTicketToggle('show_sri_status')}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                Imprimir Estado SRI (Estado: AUTORIZADO)
                                            </label>

                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer', color: tCfg.show_access_key ? '#1e293b' : '#64748b' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_access_key}
                                                    onChange={() => handleTicketToggle('show_access_key')}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                Imprimir Clave de Acceso SRI (49 dígitos)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* CONTENIDO DE PESTAÑA: 3. CLIENTE Y PEDIDO */}
                            {activeSettingsTab === 'order_customer' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <label style={{ fontSize: '0.85rem', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_date_time}
                                                onChange={() => handleTicketToggle('show_date_time')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Mostrar Fecha y Hora
                                        </label>

                                        <label style={{ fontSize: '0.85rem', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_order_number}
                                                onChange={() => handleTicketToggle('show_order_number')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Mostrar Número / Referencia de Orden
                                        </label>

                                        <label style={{ fontSize: '0.85rem', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_customer_info}
                                                onChange={() => handleTicketToggle('show_customer_info')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Mostrar Nombre del Cliente (ej. Consumidor Final)
                                        </label>

                                        <label style={{ fontSize: '0.85rem', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_customer_dni}
                                                onChange={() => handleTicketToggle('show_customer_dni')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Mostrar RUC / Cédula del Cliente
                                        </label>

                                        <label style={{ fontSize: '0.85rem', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_table_info}
                                                onChange={() => handleTicketToggle('show_table_info')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Mostrar Mesa / Tipo de Pedido (Mesa / Llevar)
                                        </label>

                                        <hr style={{ border: 0, borderTop: '1px dashed #e2e8f0', margin: '4px 0' }} />

                                        <label style={{ fontSize: '0.85rem', margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={tCfg.show_item_notes}
                                                onChange={() => handleTicketToggle('show_item_notes')}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Mostrar Notas, Salsas y Toppings de cada Producto
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* CONTENIDO DE PESTAÑA: 4. TOTALES, PIE Y CRÉDITOS */}
                            {activeSettingsTab === 'totals_footer' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>Desglose de Totales:</span>
                                        
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_subtotal}
                                                    onChange={() => handleTicketToggle('show_subtotal')}
                                                    style={{ marginRight: '6px' }}
                                                />
                                                Mostrar Subtotal
                                            </label>
                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_discount}
                                                    onChange={() => handleTicketToggle('show_discount')}
                                                    style={{ marginRight: '6px' }}
                                                />
                                                Mostrar Descuentos
                                            </label>
                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_vat}
                                                    onChange={() => handleTicketToggle('show_vat')}
                                                    style={{ marginRight: '6px' }}
                                                />
                                                Mostrar Desglose IVA
                                            </label>
                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_total}
                                                    onChange={() => handleTicketToggle('show_total')}
                                                    style={{ marginRight: '6px' }}
                                                />
                                                Mostrar Total a Pagar
                                            </label>
                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_total_cop}
                                                    onChange={() => handleTicketToggle('show_total_cop')}
                                                    style={{ marginRight: '6px' }}
                                                />
                                                Total en Pesos COP
                                            </label>
                                            <label style={{ fontSize: '0.82rem', margin: 0, cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_payment_method}
                                                    onChange={() => handleTicketToggle('show_payment_method')}
                                                    style={{ marginRight: '6px' }}
                                                />
                                                Método de Pago
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>Mensaje de Despedida / Pie de Ticket</label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_footer_message}
                                                    onChange={() => handleTicketToggle('show_footer_message')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <textarea rows="2" name="receipt_footer" value={settingsForm.receipt_footer} onChange={handleSettingsChange} placeholder="¡Gracias por su compra!..." style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }} />
                                    </div>

                                    {/* Carga de Logo Koa (Miniatura para el pie) */}
                                    <div className="form-group" style={{ padding: '8px', border: '1px dashed #cbd5e1', borderRadius: '6px', backgroundColor: '#f8fafc' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: 0 }}>
                                                <i className="bi bi-code-slash" style={{ marginRight: '4px', color: '#4a7c59' }}></i> Logo Desarrollador Koa
                                            </label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_koa_logo}
                                                    onChange={() => handleTicketToggle('show_koa_logo')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg, image/jpg"
                                                onChange={(e) => handleImageUpload(e, 'koa_logo')}
                                                style={{ fontSize: '0.8rem' }}
                                            />
                                            {settingsForm.koa_logo && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSettingsForm({ ...settingsForm, koa_logo: '' })}
                                                    style={{ padding: '2px 6px', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}
                                                >
                                                    Quitar
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem' }}>Créditos de Desarrollador</label>
                                            <label style={{ fontSize: '0.8rem', color: '#4b5563', cursor: 'pointer', margin: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={tCfg.show_developer_credits}
                                                    onChange={() => handleTicketToggle('show_developer_credits')}
                                                    style={{ marginRight: '5px' }}
                                                />
                                                Imprimir
                                            </label>
                                        </div>
                                        <input type="text" name="developer_credits" value={settingsForm.developer_credits || ''} onChange={handleSettingsChange} placeholder="Sistema desarrollado por Koa | www..." style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #d1d5db' }} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* PANEL DERECHO: VISTA PREVIA EN VIVO DINÁMICA DEL TICKET 80MM */}
                        <div style={{ position: 'sticky', top: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#334155', margin: 0 }}>
                                    <i className="bi bi-eye-fill" style={{ color: '#2563eb', marginRight: '4px' }}></i> Vista Previa en Vivo (80mm)
                                </label>
                                <span style={{ fontSize: '0.72rem', color: '#16a34a', backgroundColor: '#dcfce7', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>
                                    ● Tiempo Real
                                </span>
                            </div>

                            <div style={{
                                backgroundColor: '#fcfbf7',
                                padding: '16px 14px',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                fontFamily: '"Courier New", Courier, monospace',
                                fontSize: '11px',
                                color: '#111827',
                                lineHeight: 1.28,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                maxHeight: '550px',
                                overflowY: 'auto'
                            }}>
                                {/* CABECERA DE TURNO DESTACADO */}
                                {tCfg.show_order_seq_header && (
                                    <div style={{ textAlign: 'center', borderTop: '2px solid #000', borderBottom: '2px solid #000', padding: '4px 0', margin: '4px 0 8px 0', fontWeight: 'bold', fontSize: '14px' }}>
                                        ORDEN #12
                                    </div>
                                )}

                                {/* LOGO Y DATOS DE LA EMPRESA */}
                                <div style={{ textAlign: 'center', borderBottom: (tCfg.show_company_name || tCfg.show_tax_id || tCfg.show_company_address || tCfg.show_company_phone) ? '1px dashed #000' : 'none', paddingBottom: '6px', marginBottom: '6px' }}>
                                    {tCfg.show_company_logo && settingsForm.company_logo && (
                                        <div style={{ marginBottom: '6px' }}>
                                            <img
                                                src={settingsForm.company_logo}
                                                alt="Logo Empresa"
                                                style={{ maxWidth: '120px', maxHeight: '55px', objectFit: 'contain', filter: 'grayscale(100%) contrast(150%)' }}
                                            />
                                        </div>
                                    )}
                                    {tCfg.show_company_name && (
                                        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{settingsForm.company_name || 'MI NEGOCIO'}</div>
                                    )}
                                    {tCfg.show_tax_id && settingsForm.tax_id && <div>RUC: {settingsForm.tax_id}</div>}
                                    {tCfg.show_company_address && settingsForm.company_address && <div>{settingsForm.company_address}</div>}
                                    {tCfg.show_company_phone && settingsForm.company_phone && <div>Telf: {settingsForm.company_phone}</div>}
                                    {tCfg.show_accounting_obligation && (
                                        <div style={{ fontSize: '9px', marginTop: '2px' }}>Obligado a contabilidad: {settingsForm.obligation_contable}</div>
                                    )}
                                    {tCfg.show_tax_regime && settingsForm.contribuyente_regimen && (
                                        <div style={{ fontSize: '9px' }}>CONTRIBUYENTE {settingsForm.contribuyente_regimen}</div>
                                    )}
                                    {tCfg.show_artisan_qualification && settingsForm.artisan_qualification && (
                                        <div style={{ fontSize: '9px' }}>CALIFICACIÓN ARTESANAL NO. {settingsForm.artisan_qualification}</div>
                                    )}
                                </div>

                                {/* TÍTULO DEL DOCUMENTO Y METADATOS */}
                                <div style={{ borderBottom: '1px dashed #000', paddingBottom: '6px', marginBottom: '6px' }}>
                                    {tCfg.show_document_title && (
                                        <div style={{ fontWeight: 'bold', textAlign: 'center', fontSize: '12px', marginBottom: '4px' }}>
                                            {tCfg.custom_title || (tCfg.document_type_preset === 'factura' ? 'FACTURA ELECTRÓNICA' : 'NOTA DE VENTA')}
                                        </div>
                                    )}
                                    {tCfg.show_date_time && (
                                        <div>Fecha: {new Date().toLocaleDateString('es-EC')}  Hora: {new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}</div>
                                    )}
                                    {tCfg.show_sri_doc_number && <div>No. Factura: 001-001-000000129</div>}
                                    {tCfg.show_order_number && <div>Orden: #12 (ORD-1042)</div>}
                                    {tCfg.show_sri_status && <div>Estado: <strong>AUTORIZADO</strong></div>}
                                    {tCfg.show_access_key && (
                                        <div style={{ margin: '3px 0', fontSize: '8.5px', wordBreak: 'break-all' }}>
                                            <strong>CLAVE DE ACCESO:</strong><br />
                                            2408202601040178861700110010010000001291234567818
                                        </div>
                                    )}
                                    {tCfg.show_customer_info && <div>Cliente: CONSUMIDOR FINAL</div>}
                                    {tCfg.show_customer_dni && <div>RUC/CI: 9999999999999</div>}
                                    {tCfg.show_table_info && <div>Mesa / Tipo: Mesa 4 (Salón)</div>}
                                </div>

                                {/* PRODUCTOS DEMO */}
                                <div style={{ borderBottom: '1px dashed #000', paddingBottom: '6px', marginBottom: '6px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dotted #888', paddingBottom: '2px', marginBottom: '3px', fontWeight: 'bold', fontSize: '10px' }}>
                                        <span>PRODUCTO</span>
                                        <span>CANT  PRECIO  TOTAL</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                        <span>Hamburguesa Clásica</span>
                                        <span>1    $3.50   $3.50</span>
                                    </div>
                                    {tCfg.show_item_notes && (
                                        <div style={{ fontSize: '9.5px', color: '#444', paddingLeft: '8px' }}>+ Queso extra, Sin cebolla</div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: '3px' }}>
                                        <span>Papas Medianas</span>
                                        <span>1    $1.50   $1.50</span>
                                    </div>
                                </div>

                                {/* TOTALES */}
                                <div style={{ borderBottom: '1px dashed #000', paddingBottom: '6px', marginBottom: '6px' }}>
                                    {tCfg.show_subtotal && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>SUBTOTAL:</span>
                                            <span>$5.00</span>
                                        </div>
                                    )}
                                    {tCfg.show_discount && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>DESCUENTO:</span>
                                            <span>-$0.50</span>
                                        </div>
                                    )}
                                    {tCfg.show_vat && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span>IVA (15%):</span>
                                            <span>$0.68</span>
                                        </div>
                                    )}
                                    {tCfg.show_total && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '12px', marginTop: '3px', borderTop: '1px solid #000', paddingTop: '2px' }}>
                                            <span>TOTAL A PAGAR:</span>
                                            <span>${tCfg.show_vat ? '5.18' : '4.50'}</span>
                                        </div>
                                    )}
                                    {tCfg.show_total_cop && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#444' }}>
                                            <span>TOTAL COP:</span>
                                            <span>$18,500.00</span>
                                        </div>
                                    )}
                                </div>

                                {/* MÉTODO DE PAGO */}
                                {tCfg.show_payment_method && (
                                    <div style={{ textAlign: 'center', borderBottom: '1px dashed #000', paddingBottom: '4px', marginBottom: '6px', fontWeight: 'bold' }}>
                                        Pago en: EFECTIVO
                                    </div>
                                )}

                                {/* PIE DE TICKET */}
                                {tCfg.show_footer_message && (
                                    <div style={{ textAlign: 'center', fontSize: '9.5px', whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                        {settingsForm.receipt_footer || '¡GRACIAS POR SU COMPRA!\n*** VUELVA PRONTO ***'}
                                    </div>
                                )}

                                {/* LOGO Y CRÉDITOS KOA */}
                                {(tCfg.show_developer_credits || (tCfg.show_koa_logo && settingsForm.koa_logo)) && (
                                    <div style={{ textAlign: 'center', fontSize: '8.5px', color: '#555', borderTop: '1px dotted #888', paddingTop: '4px' }}>
                                        {tCfg.show_koa_logo && settingsForm.koa_logo && (
                                            <div style={{ marginBottom: '3px' }}>
                                                <img
                                                    src={settingsForm.koa_logo}
                                                    alt="Logo Koa"
                                                    style={{ maxWidth: '70px', maxHeight: '28px', objectFit: 'contain', filter: 'grayscale(100%) contrast(150%)' }}
                                                />
                                            </div>
                                        )}
                                        {tCfg.show_developer_credits && settingsForm.developer_credits && (
                                            <div>{settingsForm.developer_credits}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-actions" style={{ marginTop: '20px' }}>
                        <button type="button" className="btn btn-secondary" onClick={() => setIsSettingsModalOpen(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}>
                            <i className="bi bi-check2-circle" style={{ marginRight: '6px' }}></i> Guardar Configuración del Ticket
                        </button>
                    </div>
                </form>
            </Modal>

        </div>
    );
};

export default Impresoras;

const styles = `
    .page-container {
        padding: 20px;
        max-width: 1600px;
        margin: 0 auto;
    }
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }
    .page-header h2 {
        margin: 0;
        color: #1f2937;
    }
    
    .btn {
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        border: 1px solid transparent;
    }
    .btn-primary {
        background-color: var(--primary-color);
        color: white;
        border-color: var(--primary-color);
    }
    .btn-primary:hover {
        background-color: #2563eb;
    }
    .btn-secondary {
        background-color: var(--sidebar-bg);
        color: #374151;
        border-color: #d1d5db;
    }
    .btn-secondary:hover {
        background-color: #e5e7eb;
    }
    .btn-danger {
        background-color: #fee2e2;
        color: #dc2626;
        border-color: #fca5a5;
    }
    .btn-danger:hover {
        background-color: #fecaca;
    }

    /* Table */
    .table-responsive {
        width: 100%;
        overflow-x: auto;
        overflow-y: auto;
        max-height: 70vh;
        -webkit-overflow-scrolling: touch;
        border-radius: 0.5rem;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        border: 1px solid #e5e7eb;
        position: relative;
    }
    .table {
        width: 100%;
        border-collapse: collapse;
        background-color: white;
        min-width: 600px; 
    }
    .table th, .table td {
        padding: 0.75rem 1rem;
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
    }
    .table th {
        background-color: var(--sidebar-bg);
        font-weight: 600;
        color: #374151;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        position: sticky;
        top: 0;
        z-index: 10;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    .table td {
        font-size: 0.875rem;
        color: #4b5563;
    }

    /* Form */
    .form-group {
        margin-bottom: 1rem;
    }
    .form-group label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        color: #374151;
    }
    .form-control, input[type="text"], input[type="number"], select {
        width: 100%;
        padding: 0.5rem;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        font-size: 1rem;
        box-sizing: border-box;
    }
    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 1.5rem;
    }

    .alert {
        padding: 1rem;
        margin-bottom: 1rem;
        border-radius: 0.375rem;
    }
    .alert-error {
        background-color: #fee2e2;
        color: #991b1b;
        border: 1px solid #fecaca;
    }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
